import { IWorkflowNode } from "motw_core/Entity/IWorkflowNode";
import { workflowRepository } from "motw_core/Repositories/WorkflowRepository";
import * as React from "react";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import * as toastr from "toastr";
import { GetWorkflowNodeMatchesAsync } from "../Helpers/GetWorkflowNodeMatchesAsync";

const minimumSearchLength = 2;

export function useMoveNodeSearch(rootNode: IWorkflowNode) {
    const [searchText, setSearchText] = React.useState("");
    const [results, setResults] = React.useState<IWorkflowNode[]>([]);
    const [selectedParent, setSelectedParent] =
        React.useState<IWorkflowNode | null>(null);
    const [descendantIdsForSearch, setDescendantIdsForSearch] =
        React.useState<Set<number>>(
        new Set([rootNode.id])
    );
    const [descendantIds, setDescendantIds] = React.useState<number[]>([]);
    const [canSearch, setCanSearch] = React.useState(false);
    const [isSearching, setIsSearching] = React.useState(false);

    React.useEffect(() => {
        let isCurrent = true;
        setCanSearch(false);
        setDescendantIds([]);

        workflowRepository
            .GetAllChildrenAsync(rootNode, ClientDataProvider)
            .then((nodes) => {
                if (!isCurrent) {
                    return;
                }

                const descendants = nodes.filter(
                    (node) => node.id !== rootNode.id
                );
                setDescendantIds(descendants.map((node) => node.id));
                setDescendantIdsForSearch(
                    new Set(nodes.map((node) => node.id))
                );
                setCanSearch(true);
            })
            .catch(() =>
                toastr.error("Could not load valid new workflow parents")
            );

        return () => {
            isCurrent = false;
        };
    }, [rootNode.id, rootNode.parentid]);

    React.useEffect(() => {
        const query = searchText.trim();
        const isNodeId = /^#?\d+$/.test(query);

        if (
            !canSearch ||
            selectedParent?.name === searchText ||
            (query.length < minimumSearchLength && !isNodeId)
        ) {
            setResults([]);
            setIsSearching(false);
            return;
        }

        let isCurrent = true;
        setIsSearching(true);
        const timer = window.setTimeout(async () => {
            try {
                const matches = await GetWorkflowNodeMatchesAsync(query);
                if (isCurrent) {
                    setResults(
                        matches
                            .filter(
                                (node) =>
                                    !descendantIdsForSearch.has(node.id)
                            )
                            .sort(
                                (left, right) =>
                                    left.name.length - right.name.length ||
                                    left.name.localeCompare(right.name)
                            )
                            .slice(0, 8)
                    );
                }
            } finally {
                if (isCurrent) {
                    setIsSearching(false);
                }
            }
        }, 250);

        return () => {
            isCurrent = false;
            window.clearTimeout(timer);
        };
    }, [canSearch, descendantIdsForSearch, searchText, selectedParent]);

    return {
        canSearch,
        descendantIds,
        isSearching,
        results,
        searchText,
        selectedParent,
        setResults,
        setSearchText,
        setSelectedParent,
    };
}
